import { CYBER_CONSTANT } from 'constants';
import React, { useMemo, useRef } from 'react';
import { useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import {
  Typography, Grid, Spinner,
} from '@genie-ui/react';
import { useMediaQuery, useWindowResize } from '@genie-ui/react/hooks';
import SouthIcon from '@mui/icons-material/South';
import NorthIcon from '@mui/icons-material/North';
import Highcharts from 'highcharts/highcharts';
import HighchartsReact from 'highcharts-react-official';
import HighchartsAnnotation from 'highcharts/modules/annotations';
import { getTrendChartConfig } from 'utils/HighchartOptions';
import { getStateFromPercentile, roundTo10th, ordinalSuffixOf } from 'utils';

import './TrendChart.scss';

HighchartsAnnotation(Highcharts);

function TrendChartValue({
  baseValue,
  displayValue,
  nSize = 0,
}) {
  if (baseValue || baseValue === 0) {
    return !nSize
      ? CYBER_CONSTANT.LABEL.NOT_AVAILABLE : displayValue;
  }
  return '';
}

function getAvailableValueForNsize(value, nSize) {
  return nSize ? value : null;
}

function TrendChart({
  orgCurrent,
  orgTarget,
  topCurrent,
  topTarget,
  midCurrent,
  midTarget,
  bottomCurrent,
  bottomTarget,
  currentPercentile,
  targetPercentile,
  maximumValue,
  minimumValue,
  tickInterval,
  trendDirection,
  lastUpdatedAt,
  currentNSize,
  targetNSize,
  unit,
  isLoadingBenchmark,
  shouldShowCyberLegend,
  curEstimate,
  lastModifiedDt,
  kpiName,
  lastModifiedBy,
}) {
  const chartRef = useRef();
  const { showPrint } = useSelector((state) => state.cyber);
  const isMobileResponsive = useMediaQuery('(min-width:600px) and (max-width:900px)');
  const isDesktopResponsive = useMediaQuery('(min-width:1200px)');
  const isResponsive = isMobileResponsive || isDesktopResponsive;

  const config = useMemo(() => ({
    orgCurrent: (orgCurrent || orgCurrent === 0) ? +orgCurrent : undefined,
    orgTarget: (orgTarget || orgTarget === 0) ? +orgTarget : undefined,
    topCurrent: getAvailableValueForNsize(topCurrent, currentNSize),
    topTarget: getAvailableValueForNsize(topTarget, targetNSize),
    midCurrent: getAvailableValueForNsize(midCurrent, currentNSize),
    midTarget: getAvailableValueForNsize(midTarget, targetNSize),
    bottomCurrent: getAvailableValueForNsize(bottomCurrent, currentNSize),
    bottomTarget: getAvailableValueForNsize(bottomTarget, targetNSize),
    maximumValue,
    minimumValue,
    tickInterval,
    unit,
    isPrinting: showPrint,
  }), [
    orgCurrent,
    orgTarget,
    topCurrent,
    topTarget,
    midCurrent,
    midTarget,
    bottomCurrent,
    bottomTarget,
    unit,
    currentNSize,
    targetNSize,
    maximumValue,
    minimumValue,
    tickInterval,
    showPrint,
    lastModifiedDt,
    curEstimate,
    lastModifiedBy,
  ]);

  const options = useMemo(() => getTrendChartConfig(
    isResponsive,
    config,
  ), [
    isResponsive,
    config,
  ]);

  const singleAxisOptions = useMemo(() => getTrendChartConfig(
    true,
    {
      ...config,
      hideTarget: true,
      width: 150,
      height: 230,
      marginLeft: '76',
      hideTitle: true,
      backgroundColor: 'rgb(242, 242 ,242)',
    },
  ), [
    config,
  ]);

  useWindowResize(() => {
    chartRef?.current?.chart?.reflow?.();
  });

  const currentNSizeLabel = useMemo(() => {
    if (!orgCurrent?.toString()) {
      return CYBER_CONSTANT.LABEL.BENCHMARK_HIDE_FOR_NO_RESPONSE;
    }
    if (!currentNSize) {
      return CYBER_CONSTANT.LABEL.BENCHMARK_HIDE_FOR_NO_VALUE;
    }
    return currentNSize;
  }, [orgCurrent, currentNSize]);

  const targetNSizeLabel = useMemo(() => {
    if (!orgTarget?.toString()) {
      return CYBER_CONSTANT.LABEL.BENCHMARK_HIDE_FOR_NO_RESPONSE;
    }
    if (!targetNSize) {
      return CYBER_CONSTANT.LABEL.BENCHMARK_HIDE_FOR_NO_VALUE;
    }
    return targetNSize;
  }, [orgTarget, targetNSize]);

  const currentState = useMemo(
    () => getStateFromPercentile(currentPercentile).toLowerCase(),
    [currentPercentile],
  );

  const currentStatusLabel = useMemo(() => CYBER_CONSTANT
    .STATES_LABEL[
      getStateFromPercentile(currentPercentile)
    ], [currentPercentile]);

  const targetStatusLabel = useMemo(() => CYBER_CONSTANT
    .STATES_LABEL[
      getStateFromPercentile(targetPercentile)
    ], [targetPercentile]);

  const hasNoValues = useMemo(
    () => ((orgCurrent === '' || orgCurrent === undefined) && (orgTarget === '' || orgTarget === undefined)),
    [orgCurrent, orgTarget],
  );

  return (
    <div className="trendChardDiv">
      <div>
        <div className={`trendChart ${showPrint ? 'trendChart--print' : ''}`} id="trendChart">
          <HighchartsReact
            highcharts={Highcharts}
            options={options}
            ref={chartRef}
            key={`${orgCurrent}-${orgTarget}`}
          />
          {!showPrint && (
            <p className="trendChart__empty">
              {(!orgCurrent && !orgTarget) ? CYBER_CONSTANT.LABEL.ENTER_CURRENT_TARGET_VALUE : ''}
              {(!orgCurrent && orgTarget) ? CYBER_CONSTANT.LABEL.ENTER_CURRENT_VALUE : ''}
              {(orgCurrent && !orgTarget) ? CYBER_CONSTANT.LABEL.ENTER_TARGET_VALUE : ''}
            </p>
          )}
          <div className="trendChart__legendsPrint">
            <span className="trendChart__legendPrint trendChart__legendPrint--median" />
            <span>
              {CYBER_CONSTANT.LABEL.MY_ORG}
            </span>

            <span className="trendChart__legendPrint trendChart__legendPrint--mid" />
            <span>
              {CYBER_CONSTANT.LABEL.MID_PERCENTILE}
            </span>

            <span className="trendChart__legendPrint trendChart__legendPrint--yourOrg" />
            <span>
              {CYBER_CONSTANT.LABEL.MEDIAN}
            </span>
          </div>
        </div>

        {
        showPrint && (
          <div id="trendChartSingleAxis" className="trendChart__singleAxis">
            {currentPercentile ? (
              <div>
                {curEstimate && (
                  <div className={`trendChart__singleAxisLabelEstimate trendChart__singleAxisLabelEstimate--${currentState}`}>
                    <b>Estimate</b>
                  </div>
                )}
                <div className={`trendChart__singleAxisLabel trendChart__singleAxisLabel--${currentState}`}>
                  <span className="trendChart__singleAxisLabelBoldValue">
                    <span className="trendChart__singleAxisLabelValue">{Math.round(currentPercentile)}</span>
                    <sup>{ordinalSuffixOf(Math.round(currentPercentile))}</sup>
                  </span>
                  <span className="trendChart__singleAxisLabelBoldValue">
                    percentile
                  </span>
                </div>
              </div>
            ) : (
              <div className="trendChart__singleAxisEmptyValue">
                Not yet known
              </div>
            )}
            <HighchartsReact
              highcharts={Highcharts}
              options={singleAxisOptions}
              key={orgCurrent}
            />
            <div className="trendChart__singleAxisStat">
              <p>
                <span>{kpiName}</span>
                {` (${unit})`}
              </p>
              <p>{`n=${currentNSize}`}</p>

              <div className="trendChart__singleAxisDirection">
                <img src={`${process.env.PUBLIC_URL}/ppt/trend-${trendDirection}.png`} alt="direction" />
              </div>
            </div>
          </div>
        )
        }

        <div className="trendChart__directionWrapper">
          <Typography className="drawerText trendChart__direction" id="trendChartDirection">
            {CYBER_CONSTANT.TREND_CHART.LINE_1}
            <i>
              {trendDirection}
              {
                trendDirection === 'lower' ? <SouthIcon /> : <NorthIcon />
              }
            </i>
          </Typography>
        </div>
      </div>

      <div className="trendTableDiv">
        {((lastModifiedDt || lastModifiedBy) && !showPrint) && (
          <div id="trendTextLastUpdated" className="lastUpdatedText">
            <Typography className="drawerText trendText">
              {`${CYBER_CONSTANT.LABEL.LAST_UPDATED_ON} `}
            </Typography>
            {lastModifiedDt
              && (
                <Typography className="drawerText trendText">
                  on
                  <b>{` ${lastModifiedDt} `}</b>
                </Typography>
              )}
            {lastModifiedBy
              && (
                <Typography className="drawerText trendText">
                  by
                  <b>{` ${lastModifiedBy}`}</b>
                </Typography>
              )}
          </div>
        )}
        {((lastModifiedDt && lastModifiedBy) && showPrint) && (
          <span id="trendTextLastUpdated" className="lastUpdatedText">
            {`${CYBER_CONSTANT.LABEL.LAST_UPDATED_ON} on ${lastModifiedDt} by ${lastModifiedBy}`}
          </span>
        )}
        {((lastModifiedDt && !lastModifiedBy) && showPrint) && (
          <span id="trendTextLastUpdated" className="lastUpdatedText">
            {`${CYBER_CONSTANT.LABEL.LAST_UPDATED_ON} on ${lastModifiedDt}`}
          </span>
        )}
        {((!lastModifiedDt && lastModifiedBy) && showPrint) && (
          <span id="trendTextLastUpdated" className="lastUpdatedText">
            {`${CYBER_CONSTANT.LABEL.LAST_UPDATED_ON} on ${lastModifiedBy}`}
          </span>
        )}

        {shouldShowCyberLegend && !hasNoValues && (
        <div className="trendChart__table">
          <div className="trendChart__tableWrapper" id="trendChartTable">
            <div className="trendChart__tableRow">
              <div className="trendChart__tableRowHead"> </div>
              <div className="trendChart__tableRowHead">
                <b>{CYBER_CONSTANT.LABEL.CURRENT}</b>
                {orgCurrent >= 0 ? ` (n=${currentNSizeLabel})` : ''}
              </div>
              <div className="trendChart__tableRowHead">
                <b>{CYBER_CONSTANT.LABEL.TARGET}</b>
                {orgTarget >= 0 ? ` (n=${targetNSizeLabel})` : ''}
              </div>
            </div>

            <div className="trendChart__myOrgRow">
              <div className="trendChart__tableRowCell">{CYBER_CONSTANT.LABEL.MY_ORG_STATUS}</div>
              <div className="trendChart__tableRowCell">
                <TrendChartValue
                  nSize={currentNSize}
                  baseValue={orgCurrent}
                  displayValue={currentPercentile !== undefined ? currentStatusLabel : ''}
                />
              </div>
              <div className="trendChart__tableRowCell">
                <TrendChartValue
                  nSize={targetNSize}
                  baseValue={orgTarget}
                  displayValue={targetPercentile !== undefined ? targetStatusLabel : ''}
                />
              </div>
            </div>
            <div className="trendChart__tableRow">
              <div className="trendChart__tableRowCell">{CYBER_CONSTANT.LABEL.MY_ORG_PERCENTILE}</div>
              <div className="trendChart__tableRowCell">
                {
                  isLoadingBenchmark ? <div className="trendChart__loader"><Spinner /></div>
                    : (
                      <TrendChartValue
                        nSize={currentNSize}
                        baseValue={orgCurrent}
                        displayValue={(
                          <span>
                            {currentPercentile !== undefined
                              ? (
                                <span>
                                  {Math.round(currentPercentile)}
                                  <sup>{ordinalSuffixOf(Math.round(currentPercentile))}</sup>
                                </span>
                              )
                              : ''}
                          </span>
                        )}
                      />

                    )
                }
              </div>
              <div className="trendChart__tableRowCell">
                {
                  isLoadingBenchmark ? <div className="trendChart__loader"><Spinner /></div>
                    : (
                      <TrendChartValue
                        nSize={targetNSize}
                        baseValue={orgTarget}
                        displayValue={(
                          <span>
                            {targetPercentile !== undefined
                              ? (
                                <span>
                                  {Math.round(targetPercentile)}
                                  <sup>{ordinalSuffixOf(Math.round(targetPercentile))}</sup>
                                </span>
                              )
                              : ''}
                          </span>
                        )}
                      />

                    )
                }
              </div>
            </div>
            <div className="trendChart__myOrgRow">
              <div className="trendChart__tableRowCell">
                {`${CYBER_CONSTANT.LABEL.MY_ORG} ${curEstimate ? `(${CYBER_CONSTANT.LABEL.ESTIMATE})` : ''}`}
              </div>
              <div className="trendChart__tableRowCell">
                {roundTo10th(orgCurrent)}
              </div>
              <div className="trendChart__tableRowCell">
                {roundTo10th(orgTarget)}
              </div>
            </div>
            <div className="trendChart__tableRow">
              <div className="trendChart__tableRowCell">{CYBER_CONSTANT.LABEL.TOP_PEER}</div>
              <div className="trendChart__tableRowCell">
                {isLoadingBenchmark
                  ? <div className="trendChart__loader"><Spinner /></div>
                  : (
                    <TrendChartValue
                      nSize={currentNSize}
                      baseValue={orgCurrent}
                      displayValue={topCurrent}
                    />
                  )}
              </div>
              <div className="trendChart__tableRowCell">
                {isLoadingBenchmark
                  ? <div className="trendChart__loader"><Spinner /></div>
                  : (
                    <TrendChartValue
                      nSize={targetNSize}
                      baseValue={orgTarget}
                      displayValue={topTarget}
                    />
                  )}
              </div>
            </div>
            <div className="trendChart__myOrgRow">
              <div className="trendChart__tableRowCell">{CYBER_CONSTANT.LABEL.MID_PEER}</div>
              <div className="trendChart__tableRowCell">
                {isLoadingBenchmark
                  ? <div className="trendChart__loader"><Spinner /></div>
                  : (
                    <TrendChartValue
                      nSize={currentNSize}
                      baseValue={orgCurrent}
                      displayValue={midCurrent}
                    />
                  )}
              </div>
              <div className="trendChart__tableRowCell">
                {isLoadingBenchmark
                  ? <div className="trendChart__loader"><Spinner /></div>
                  : (
                    <TrendChartValue
                      nSize={targetNSize}
                      baseValue={orgTarget}
                      displayValue={midTarget}
                    />
                  )}
              </div>
            </div>
            <div className="trendChart__tableRow">
              <div className="trendChart__tableRowCell">{CYBER_CONSTANT.LABEL.BOTTOM_PEER}</div>
              <div className="trendChart__tableRowCell">
                {isLoadingBenchmark
                  ? <div className="trendChart__loader"><Spinner /></div>
                  : (
                    <TrendChartValue
                      nSize={currentNSize}
                      baseValue={orgCurrent}
                      displayValue={bottomCurrent}
                    />
                  )}

              </div>
              <div className="trendChart__tableRowCell">
                {isLoadingBenchmark
                  ? <div className="trendChart__loader"><Spinner /></div>
                  : (
                    <TrendChartValue
                      nSize={targetNSize}
                      baseValue={orgTarget}
                      displayValue={bottomTarget}
                    />
                  )}
              </div>
            </div>
          </div>
        </div>
        )}

        <Grid>
          <Grid item className="trendTextDiv">
            <div id="trendTextExplanation">
              <Typography className="drawerText trendText">
                {CYBER_CONSTANT.LABEL.MY_ORG_STATUS_EXPLANATION}
              </Typography>
            </div>
            <div id="trendTextSummary" className="asOfSourceDiv">
              <Typography className="drawerText trendText">
                {CYBER_CONSTANT.TREND_CHART.LINE_3}
                {lastUpdatedAt}
              </Typography>
              <Typography className="drawerText trendText">{CYBER_CONSTANT.TREND_CHART.LINE_4}</Typography>
            </div>
          </Grid>
        </Grid>
      </div>
    </div>
  );
}

TrendChartValue.propTypes = {
  baseValue: PropTypes.string.isRequired,
  displayValue: PropTypes.string.isRequired,
  nSize: PropTypes.number,
};

TrendChart.propTypes = {
  orgCurrent: PropTypes.string,
  orgTarget: PropTypes.string,
  topCurrent: PropTypes.number,
  topTarget: PropTypes.number,
  midCurrent: PropTypes.number,
  midTarget: PropTypes.number,
  bottomCurrent: PropTypes.number,
  bottomTarget: PropTypes.number,
  currentPercentile: PropTypes.number,
  targetPercentile: PropTypes.number,
  maximumValue: PropTypes.number,
  minimumValue: PropTypes.number,
  tickInterval: PropTypes.number,
  trendDirection: PropTypes.oneOf([
    CYBER_CONSTANT.TREND_DIRECTION.HIGHER,
    CYBER_CONSTANT.TREND_DIRECTION.LOWER,
  ]),
  lastUpdatedAt: PropTypes.number,
  unit: PropTypes.string,
  targetNSize: PropTypes.number,
  currentNSize: PropTypes.number,
  isLoadingBenchmark: PropTypes.bool,
  shouldShowCyberLegend: PropTypes.bool,
  curEstimate: PropTypes.bool,
  lastModifiedDt: PropTypes.number,
  lastModifiedBy: PropTypes.string,
  kpiName: PropTypes.string,
};

TrendChart.defaultProps = {
  orgCurrent: undefined,
  orgTarget: undefined,
  topCurrent: undefined,
  topTarget: undefined,
  midCurrent: undefined,
  midTarget: undefined,
  bottomCurrent: undefined,
  bottomTarget: undefined,
  currentPercentile: undefined,
  targetPercentile: undefined,
  maximumValue: undefined,
  minimumValue: undefined,
  tickInterval: undefined,
  trendDirection: CYBER_CONSTANT.TREND_DIRECTION.HIGHER,
  lastUpdatedAt: 'Jan 2023',
  currentNSize: 0,
  targetNSize: 0,
  unit: '%',
  isLoadingBenchmark: false,
  shouldShowCyberLegend: false,
  curEstimate: false,
  lastModifiedDt: 0,
  kpiName: '',
  lastModifiedBy: '',
};

export default TrendChart;
